import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";
import type { PortalState, UWAnswers } from "@/store/portal-store";

interface Step5Props {
  state: PortalState;
  updateUWAnswers: (key: keyof UWAnswers, value: string) => void;
  onNext: () => void;
}

function RadioGroup({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-base">{label}</Label>
      <div className="space-y-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "w-full text-left px-4 py-3 rounded-xl border text-sm transition-all",
              value === opt.value
                ? "border-blue-500 bg-blue-50 text-blue-700 font-medium"
                : "border-gray-200 bg-white hover:border-gray-300"
            )}
          >
            <span className="flex items-center gap-3">
              <span
                className={cn(
                  "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                  value === opt.value ? "border-blue-500" : "border-gray-300"
                )}
              >
                {value === opt.value && (
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                )}
              </span>
              {opt.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PillGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-base">{label}</Label>
      <div className="flex gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "flex-1 px-3 py-2.5 rounded-full border text-sm font-medium transition-all",
              value === opt.value
                ? "border-blue-500 bg-blue-500 text-white"
                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Step5UWQuestions({ state, updateUWAnswers, onNext }: Step5Props) {
  const [loading, setLoading] = useState(false);
  const uw = state.uwAnswers;

  const allAnswered =
    uw.driverStatus &&
    (uw.driverStatus !== "leased" || uw.carrierName.length >= 2) &&
    uw.operationScope &&
    /^\d{5}$/.test(uw.garagingZip) &&
    uw.priorInsurance &&
    uw.losses &&
    (uw.losses !== "yes" || (uw.lossCount && parseInt(uw.lossCount) >= 1)) &&
    uw.cmvExperience &&
    uw.ticketsViolations;

  const handleSubmit = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onNext();
    }, 2000);
  };

  return (
    <div className="flex flex-col px-6 py-6 pb-24">
      <h2 className="text-xl font-bold mb-1">A Few Quick Questions</h2>
      <p className="text-gray-500 text-sm mb-6">
        Answer these to get your personalized quote.
      </p>

      <div className="space-y-6">
        {/* Q1 */}
        <RadioGroup
          label="1. What is your driver status?"
          name="driverStatus"
          options={[
            { value: "owner", label: "Owner-operator" },
            { value: "leased", label: "Leased on to a motor carrier" },
          ]}
          value={uw.driverStatus}
          onChange={(v) => updateUWAnswers("driverStatus", v)}
        />
        {uw.driverStatus === "leased" && (
          <div className="space-y-2 pl-2 border-l-2 border-blue-200 ml-2 animate-in slide-in-from-top-2">
            <Label htmlFor="carrierName">Motor carrier name</Label>
            <Input
              id="carrierName"
              placeholder="Carrier name"
              value={uw.carrierName}
              onChange={(e) => updateUWAnswers("carrierName", e.target.value)}
              className="h-12"
            />
          </div>
        )}

        {/* Q2 */}
        <RadioGroup
          label="2. What is your operation scope?"
          name="operationScope"
          options={[
            { value: "interstate", label: "Interstate for-hire" },
            { value: "intrastate", label: "Intrastate only" },
          ]}
          value={uw.operationScope}
          onChange={(v) => updateUWAnswers("operationScope", v)}
        />

        {/* Q3 */}
        <div className="space-y-2">
          <Label htmlFor="garagingZip" className="text-base">
            3. Garaging ZIP code
          </Label>
          <Input
            id="garagingZip"
            type="text"
            inputMode="numeric"
            placeholder="ZIP code"
            value={uw.garagingZip}
            onChange={(e) =>
              updateUWAnswers(
                "garagingZip",
                e.target.value.replace(/\D/g, "").slice(0, 5)
              )
            }
            className="h-12 text-base"
          />
          <p className="text-xs text-gray-400">
            Pre-filled from your driver's license address.
          </p>
        </div>

        {/* Q4 */}
        <RadioGroup
          label="4. Prior insurance status?"
          name="priorInsurance"
          options={[
            { value: "insured30", label: "Insured within last 30 days" },
            { value: "lapse30", label: "Lapse greater than 30 days" },
            { value: "newVenture", label: "New venture" },
          ]}
          value={uw.priorInsurance}
          onChange={(v) => updateUWAnswers("priorInsurance", v)}
        />

        {/* Q5 */}
        <RadioGroup
          label="5. Any losses in the last 3 years?"
          name="losses"
          options={[
            { value: "no", label: "No losses" },
            { value: "yes", label: "Yes" },
          ]}
          value={uw.losses}
          onChange={(v) => updateUWAnswers("losses", v)}
        />
        {uw.losses === "yes" && (
          <div className="space-y-2 pl-2 border-l-2 border-blue-200 ml-2 animate-in slide-in-from-top-2">
            <Label htmlFor="lossCount">How many losses?</Label>
            <Input
              id="lossCount"
              type="text"
              inputMode="numeric"
              placeholder="Number of losses"
              value={uw.lossCount}
              onChange={(e) =>
                updateUWAnswers(
                  "lossCount",
                  e.target.value.replace(/\D/g, "").slice(0, 2)
                )
              }
              className="h-12"
            />
          </div>
        )}

        {/* Q6a */}
        <PillGroup
          label="6a. CMV driving experience?"
          options={[
            { value: "lt1", label: "<1 yr" },
            { value: "1to3", label: "1-3" },
            { value: "3to5", label: "3-5" },
            { value: "5plus", label: "5+" },
          ]}
          value={uw.cmvExperience}
          onChange={(v) => updateUWAnswers("cmvExperience", v)}
        />

        {/* Q6b */}
        <RadioGroup
          label="6b. Any tickets or violations in the last 3 years?"
          name="ticketsViolations"
          options={[
            { value: "no", label: "No" },
            { value: "speeding", label: "Yes - Speeding" },
            { value: "outOfService", label: "Yes - Out of Service" },
            { value: "other", label: "Yes - Other" },
          ]}
          value={uw.ticketsViolations}
          onChange={(v) => updateUWAnswers("ticketsViolations", v)}
        />
      </div>

      {/* Sticky bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 pb-6">
        <Button
          className="w-full h-12 text-base max-w-md mx-auto block"
          disabled={!allAnswered || loading}
          onClick={handleSubmit}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner size="sm" className="border-white border-t-transparent" />
              Getting your quote...
            </span>
          ) : (
            "Get My Quote"
          )}
        </Button>
      </div>
    </div>
  );
}
